import React, { useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import Select from 'react-select'
import { ClientService } from '../../component/services/client.service'
import DonutChart, { ChartDataItem } from '../../component/Charts/DonutChart'
import CetLoader from '../../component/Loader/CetLoader'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faClipboardCheck, faUserCheck, faUniversity } from '@fortawesome/free-solid-svg-icons'
import ReverificationUsersModal from '../../component/modals/ReverificationUsers.modal'
import { useTheme } from '../../theme/useTheme'

export default function CollegeOverview() {
    const { colors } = useTheme()
    const [colleges, setColleges] = useState<any[]>([])
    const [selectedCollege, setSelectedCollege] = useState<any>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [collegeStats, setCollegeStats] = useState<any>(null)
    const [chartData, setChartData] = useState<ChartDataItem[]>([])
    const [showReverificationModal, setShowReverificationModal] = useState<any>(undefined)

    async function fetchColleges() {
        setLoading(true)
        try {
            const res: any = await ClientService.getReverificationColleges()
            if (res.status === 200) {
                setColleges(res.data?.data?.map((c: any) => ({ value: c?.collegId, label: c?.collegeName })) || [])
            }
        } catch (e) {}
        finally { setLoading(false) }
    }

    async function getCollegeStats() {
        setLoading(true)
        try {
            const res: any = await ClientService.getReverificationCollegeStats(selectedCollege)
            if (res.status === 200) {
                const s = res.data?.data || null
                setCollegeStats(s)
                if (s) {
                    const v = s.uniqueVerifications || 0
                    const t = s.totalStudentsCount || 0
                    setChartData([
                        { category: 'Verified', value: v, fill: '#10b981' },
                        { category: 'Absent', value: t - v, fill: '#f59e0b' },
                    ])
                }
            }
        } catch (e) {}
        finally { setLoading(false) }
    }

    useEffect(() => { fetchColleges() }, [])
    useEffect(() => { if (selectedCollege) getCollegeStats() }, [selectedCollege])

    const verified = collegeStats?.uniqueVerifications || 0
    const total = collegeStats?.totalStudentsCount || 0
    const pct = total > 0 ? ((verified / total) * 100).toFixed(1) : '0.0'

    return (
        <>
            <div style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
                    <div>
                        <p style={{ margin: 0, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: colors.text.secondary, textTransform: 'uppercase' }}>Per Venue</p>
                        <h5 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: colors.text.primary }}>Venue Overview</h5>
                    </div>
                    <div style={{ minWidth: 220 }}>
                        <Select
                            options={colleges}
                            placeholder="Select venue..."
                            onChange={(e: any) => setSelectedCollege(e?.value || null)}
                            isClearable
                            value={colleges.find((c: any) => c.value === selectedCollege) || null}
                            styles={selectStyles(colors)}
                        />
                    </div>
                </div>

                {loading ? (
                    <CetLoader />
                ) : selectedCollege && collegeStats ? (
                    <>
                        <Row className="g-2 mb-3">
                            {[
                                { label: 'Total Students', value: total.toLocaleString(), icon: faClipboardCheck, accent: colors.primary.main, bg: `rgba(72,107,173,0.08)`, onClick: undefined },
                                { label: 'Verified', value: verified.toLocaleString(), icon: faCheckCircle, accent: '#10b981', bg: 'rgba(16,185,129,0.08)', onClick: () => setShowReverificationModal(selectedCollege) },
                                { label: 'Absent', value: (total - verified).toLocaleString(), icon: faUniversity, accent: '#f59e0b', bg: 'rgba(245,158,11,0.08)', onClick: undefined },
                                { label: 'Attendance', value: `${pct}%`, icon: faUserCheck, accent: colors.primary.light, bg: `rgba(91,143,199,0.08)`, onClick: undefined },
                            ].map((s) => (
                                <Col xs={6} key={s.label}>
                                    <div
                                        onClick={s.onClick}
                                        style={{
                                            background: s.bg,
                                            borderRadius: 12,
                                            padding: '12px 14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            border: `1px solid ${s.accent}22`,
                                            cursor: s.onClick ? 'pointer' : 'default',
                                            transition: 'box-shadow 0.15s',
                                        }}
                                    >
                                        <div style={{ width: 36, height: 36, borderRadius: 9, background: `${s.accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <FontAwesomeIcon icon={s.icon} style={{ color: s.accent, fontSize: 16 }} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 18, fontWeight: 700, color: colors.text.primary, lineHeight: 1.1 }}>{s.value}</div>
                                            <div style={{ fontSize: 11, color: colors.text.secondary, marginTop: 2 }}>{s.label}</div>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        {chartData.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <DonutChart
                                    data={chartData}
                                    height={220}
                                    width={220}
                                    showTooltip={true}
                                    showLegend={true}
                                    showCalloutLabels={false}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <EmptyState message={selectedCollege ? 'Loading venue data...' : 'Select a venue to view its statistics.'} />
                )}
            </div>

            <ReverificationUsersModal
                show={showReverificationModal}
                handleClose={() => setShowReverificationModal(undefined)}
            />
        </>
    )
}

const cardStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #e5e7eb',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    padding: 24,
    marginTop: 24,
    height: '100%',
}

const selectStyles = (colors: any) => ({
    control: (base: any) => ({
        ...base,
        borderRadius: 8,
        border: `1px solid ${colors.border.secondary}`,
        boxShadow: 'none',
        fontSize: 13,
        '&:hover': { borderColor: colors.primary.main },
    }),
    option: (base: any, state: any) => ({
        ...base,
        fontSize: 13,
        backgroundColor: state.isSelected ? colors.primary.main : state.isFocused ? colors.background.secondary : '#fff',
        color: state.isSelected ? '#fff' : colors.text.primary,
    }),
})

const EmptyState = ({ message }: { message: string }) => (
    <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontSize: 14 }}>{message}</div>
)
